import { useSelection } from '@/app/SelectionContext';
import { useAlerts } from '@/app/hooks';
import { PageHeader } from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { Icon } from '@/components/Icon';
import { formatPercent, periodLabel, toNumber } from '@/utils/format';
import { v } from '@/theme/tokens';

const SEVERITY: Record<string, { tone: 'negative' | 'warning' | 'accent'; label: string }> = {
  CRITICAL: { tone: 'negative', label: 'Критично' },
  WARNING: { tone: 'warning', label: 'Внимание' },
  INFO: { tone: 'accent', label: 'Инфо' },
};

export function AlertsPage() {
  const { selectedPeriod, units } = useSelection();
  const query = useAlerts(selectedPeriod?.id);

  return (
    <div style={{ animation: 'fade-in 0.35s ease' }}>
      <PageHeader
        title="Требует внимания"
        subtitle={
          selectedPeriod
            ? `Правила контроля · ${periodLabel(selectedPeriod.year, selectedPeriod.month)}`
            : 'Правила контроля'
        }
      />

      {query.isPending && <Spinner />}
      {query.error && (
        <ErrorState
          message={query.error instanceof Error ? query.error.message : 'Ошибка загрузки'}
          onRetry={() => void query.refetch()}
        />
      )}

      {query.data && query.data.alerts.length === 0 && (
        <EmptyState title="Всё в порядке" description="Ни одно правило контроля не сработало за выбранный период." icon="check" />
      )}

      {query.data?.alerts.map((alert, index) => {
        const severity = SEVERITY[alert.severity] ?? SEVERITY.INFO;
        const unit = units.find((u) => u.id === alert.business_unit_id);
        return (
          <Card key={index} padding={16} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: alert.severity === 'CRITICAL' ? v('negative-soft') : alert.severity === 'WARNING' ? v('warning-soft') : v('accent-soft') }}>
                <Icon name="alert" size={17} color={alert.severity === 'CRITICAL' ? v('negative') : alert.severity === 'WARNING' ? v('warning') : v('accent')} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 14.5 }}>{alert.title}</span>
                  <Badge tone={severity.tone}>{severity.label}</Badge>
                  {unit && <Badge tone="neutral">{unit.name}</Badge>}
                </div>
                <div style={{ fontSize: 13.5, color: v('ink-sub'), marginTop: 5 }}>{alert.detail}</div>
                {alert.value != null && alert.threshold != null && (
                  <div style={{ fontSize: 12, color: v('ink-muted'), marginTop: 5 }}>
                    Правило: {alert.rule} · значение {formatPercent(toNumber(alert.value))} · порог {formatPercent(toNumber(alert.threshold))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
